<template>
    <div class="intent-group-wrap">
        <div class="intent-group">
            <div class="w-50 utterance">
                <patternGroup
                    v-if="isPatternView"
                    :patterns="patternsInIntent"
                    :context="category"
                    :intent="intent"
                    :wrapBottomFromHeaderSpace="wrapBottomFromHeaderSpace"
                />
                <utterances-group
                    v-else
                    :utterancesInIntent="utterancesInIntent"
                    :context="category"
                    :intent="intent"
                    :wrapTopReachHeader="wrapTopReachHeader"
                    :wrapBottomFromHeaderSpace="wrapBottomFromHeaderSpace"
                    :scrollTop="scrollTop"
                />
            </div>
            <div class="w-50 intent">
                <div class="intent--block">
                    <intent
                        class="intent--form"
                        :intent.sync="$v.localIntent.$model"
                        :errors="intentErrors"
                        :isIntentEditing="isIntentEditing"
                        :flowEntityUpdating="flowEntityUpdating"
                        @save="handleIntentUpdate()"
                        @reset="closeAndResetForm"
                        @add-flow="openFlowEntityModal"
                    />
                    <intent-actions
                        v-if="!isIntentEditing"
                        :isNewIntent="$props.isNewIntent"
                        :new-confirmation-phrase="!confirmationPhrase"
                        :intent="localIntent"
                        :are-features-enabled="isCurrentKMWithEntityExtractions"
                        :is-intent-updating="isIntentUpdating"
                        @edit-intent="isIntentEditing = true"
                        @edit-confirmation-phrase="isConfPhraseModalOpened = true"
                        @add-flow="openFlowEntityModal"
                        @delete="deleteIntentHandler"
                    />
                </div>
                <confirmation-phrase
                    :opened="isConfPhraseModalOpened"
                    :updating="isIntentUpdating"
                    :newPhrase="!confirmationPhrase"
                    :confirmationPhrase="confirmationPhrase"
                    @save="updateConfirmationPhrase"
                    @delete="deleteConfPhrase"
                    @close="closeConfirmationPhraseModal"
                />
                <flow-entity-modal
                    targer="intent"
                    :id="intent.id"
                />
                <or-confirm
                  ref="delete-response-confirm"
                  autofocus="confirm-button"
                  confirm-button-text="Confirm"
                  deny-button-text="Deny"
                  :title="$t('common.intent.intent_group.deleteModal.title')"
                  @confirm="confirmDeleteResponse"
                  @deny="denyDeleteResponse"
                >
                  {{ $t('common.intent.intent_group.deleteModal.description') }}
                </or-confirm>
            </div>
        </div>
    </div>
</template>

<script>
import KnowledgeBus from '@/KnowledgeBus'
import { mapActions, mapState, mapGetters } from 'vuex'
import { intentValidationMixin } from '@/mixins/intent-validation.mixin'
import ConfirmationPhrase from '@/components/common/modals/confirmation_phrase_modal'
import utterancesGroup from '../utterance/utterances_group'
import patternGroup from '../pattern/pattern_group'
import intent from './intent'
import IntentActions from './intent_actions'
import flowEntityModal from '@/components/model/nlu-model/sidebar/flow_entities/flow_entity_modal'

export default {
  mixins: [intentValidationMixin],

  components: {
    ConfirmationPhrase,
    utterancesGroup,
    patternGroup,
    intent,
    IntentActions,
    flowEntityModal
  },

  props: {
    isNewIntent: Boolean,
    category: Object,
    intent: Object,
    utterances: Object,
    scrollTop: Number,
    wrapTopReachHeader: Boolean,
    wrapBottomFromHeaderSpace: Number,
    isPatternView: Boolean
  },

  created () {
    KnowledgeBus.$on('intent-flow-entity::append', this.appendFlowEntity)
    KnowledgeBus.$on('intent-flow-entity::remove', this.removeFlowEntity)
  },

  beforeDestroy () {
    KnowledgeBus.$off('intent-flow-entity::append', this.appendFlowEntity)
    KnowledgeBus.$off('intent-flow-entity::remove', this.removeFlowEntity)
  },

  data () {
    return {
      localIntent: _.cloneDeep(this.$props.intent),
      isConfPhraseModalOpened: false,
      isIntentEditing: false,
      isIntentUpdating: false,
      flowEntityUpdating: false
    }
  },

  computed: {
    ...mapState('knowledgebase/patterns', ['patterns']),
    ...mapState('knowledgebase/entities', ['allEntities']),
    ...mapGetters('knowledgebase', ['isCurrentKMWithEntityExtractions']),

    utterancesInIntent () {
      return _.filter(this.$props.utterances.value, utterance =>
        !utterance.autoGenerated && (utterance.intent === this.intent.id))
    },

    patternsInIntent () {
      return _.filter(this.patterns.value, pattern =>
        pattern.intent === this.intent.id)
    },

    confirmationPhrase: {
      get () {
        return (this.localIntent.customData ? this.localIntent.customData.confirmationPhrase : '') || ''
      },
      set (value) {
        if (!this.localIntent.customData) {
          this.localIntent.customData = {}
        }
        this.localIntent.customData.confirmationPhrase = value
      }
    }
  },

  methods: {
    ...mapActions('knowledgebase', ['updateIntent', 'deleteIntent']),

    deleteIntentHandler () {
      this.$refs['delete-response-confirm'].open()
    },

    confirmDeleteResponse () {
      this.deleteIntent({
        id: this.localIntent.id,
        appId: this.localIntent.category
      })
    },

    denyDeleteResponse () {
      this.$refs['delete-response-confirm'].close()
    },

    async appendFlowEntity ({ targetId, flowEntity }) {
      if (targetId === this.intent.id) {
        this.flowEntityUpdating = true
        await this.updateIntent({
          appId: this.localIntent.category,
          id: this.localIntent.id,
          name: this.localIntent.name,
          type: this.localIntent.type,
          processFlows: [flowEntity],
          customData: this.intent.customData
        })
        this.localIntent = _.cloneDeep(this.$props.intent)
        this.flowEntityUpdating = false
      }
    },

    async removeFlowEntity (targetId) {
      if (targetId === this.intent.id) {
        this.flowEntityUpdating = true
        await this.updateIntent({
          appId: this.localIntent.category,
          id: this.localIntent.id,
          name: this.localIntent.name,
          type: this.localIntent.type,
          processFlows: [],
          customData: this.intent.customData
        })
        this.localIntent = _.cloneDeep(this.$props.intent)
        this.flowEntityUpdating = false
      }
    },

    openFlowEntityModal () {
      const currentflowEntity = _.isEmpty(this.intent.postprocessFlows)
        ? ''
        : this.intent.postprocessFlows[0]

      KnowledgeBus.$emit('flow-entity-modal::open', {
        currentflowEntity: currentflowEntity,
        target: 'intent',
        targetId: this.intent.id
      })
    },

    updateConfirmationPhrase (newPhrase) {
      this.confirmationPhrase = newPhrase
      this.handleIntentUpdate()
    },

    closeConfirmationPhraseModal () {
      this.isConfPhraseModalOpened = false
    },

    deleteConfPhrase () {
      this.confirmationPhrase = null
      this.handleIntentUpdate()
    },

    async handleIntentUpdate () {
      this.$v.localIntent.$touch()
      if (this.$v.localIntent.$invalid) {
        return
      }
      this.isIntentEditing = false
      this.isIntentUpdating = true
      this.isConfPhraseModalOpened = false

      await this.updateIntent({
        appId: this.intent.category || this.intent.appId,
        id: this.intent.id,
        name: _.trim(this.localIntent.name),
        type: this.localIntent.type,
        processFlows: this.localIntent.postprocessFlows,
        customData: this.localIntent.customData
      })
      this.localIntent = _.cloneDeep(this.$props.intent)
      this.isIntentUpdating = false
    },

    closeAndResetForm () {
      this.isIntentEditing = false
      this.localIntent = _.cloneDeep(this.$props.intent)
      this.$v.localIntent.$reset()
    }
  },

  watch: {
    intent (updatedIntent) {
      this.localIntent = _.cloneDeep(updatedIntent)
    }
  }
}
</script>

<style lang="scss" scoped>
    .intent-group-wrap {
        display: flex;
        flex-grow: 1;
        height: 100%;
        border-bottom: solid 1px $or-grey;

        .intent-group {
            display: flex;
            flex-grow: 1;

            .intent--block {
                display: flex;
                height: inherit;

                .intent--form {
                    width: 100%;
                    height: inherit;
                }
            }
        }

        .w-50 {
            width: 50%;

            &.utterance {
                height: 100%;
            }

            &.intent {
                max-height: inherit;
            }

            &:first-child {
                border-right: solid 1px $or-grey;
            }
        }
    }
</style>
